import { Routes } from "@angular/router";
import { logoutGuard } from "./core/guards/logout.guard";
import { loginGuard } from "./core/guards/login.guard";
import { curriculumResolver } from "./feature/services/curriculum/curriculum.resolver";
import { subjectResolver } from "./feature/services/subjects/subject.resolver";
import { revisionNotesResolver } from "./feature/services/revision-notes/revision-notes.resolver";
import { revNotesDetailsResolver } from "./feature/services/revision-notes/rev-notes-details.resolver";
import { revNotesLevelDetailsResolver } from "./feature/services/revision-notes/rev-notes-level-details.resolver";
import { examQuestionsResolver } from "./feature/services/exam-questions/exam-questions.resolver";
import { levelExamQuestionsResolver } from "./feature/services/exam-questions/level-exam-questions.resolver";
import { examQuestionsDetailsResolver } from "./feature/services/exam-questions/exam-questions-details.resolver";
import { flashcardsResolver } from "./feature/services/flashcards/flashcards.resolver";
import { levelFlashcardsResolver } from "./feature/services/flashcards/level-flashcards.resolver";
import { flashcardsDetailsResolver } from "./feature/services/flashcards/flashcards-details.resolver";
import { pastpapersResolver } from "./feature/services/pastpapers/pastpapers.resolver";
import { pastpapersDetailsResolver } from "./feature/services/pastpapers/pastpapers-details.resolver";
import { profileResolver } from "./core/services/profile/profile.resolver";

export const routes: Routes = [
  {
    path: "",
    loadComponent: () => import("./feature/pages/landing/landing.component").then((p) => p.LandingComponent),
    title: "Exemplix",
  },
  {
    path: "curriculum",
    loadComponent: () =>
      import("./feature/pages/curriculum-tabs/curriculum-tabs.component").then((p) => p.CurriculumTabsComponent),
    title: "Curriculum",
    resolve: { data: curriculumResolver },
    canActivate: [loginGuard],
  },
  {
    path: "home",
    loadComponent: () => import("./feature/pages/home/home.component").then((p) => p.HomeComponent),
    title: "Home",
    resolve: { data: profileResolver },
    canActivate: [loginGuard],
  },
  {
    path: "curriculum/:cur/subject/:sub",
    loadComponent: () => import("./feature/pages/subject/subject.component").then((p) => p.SubjectComponent),
    title: "Subject",
    canActivate: [loginGuard],
    resolve: { data: subjectResolver },
  },
  {
    path: "curriculum/:cur/subject/:sub/revision-notes",
    loadComponent: () =>
      import("./feature/pages/revision-notes/revision-notes.component").then((p) => p.RevisionNotesComponent),
    title: "Revision Notes",
    canActivate: [loginGuard],
    resolve: { data: revisionNotesResolver },
  },
  {
    path: "revision-notes/:sub/:subTopic",
    loadComponent: () =>
      import("./feature/pages/level-revision-details/level-revision-details.component").then(
        (p) => p.LevelRevisionDetailsComponent,
      ),
    title: "Level Revision Details",
    canActivate: [loginGuard],
    resolve: { data: revNotesLevelDetailsResolver },
    runGuardsAndResolvers: "paramsOrQueryParamsChange",
  },
  {
    path: "revision-notes/:topic",
    loadComponent: () =>
      import("./feature/pages/revision-notes-details/revision-notes-details.component").then(
        (p) => p.RevisionNotesDetailsComponent,
      ),
    title: "Revision Notes Details",
    canActivate: [loginGuard],
    resolve: { data: revNotesDetailsResolver },
  },
  {
    path: "curriculum/:cur/subject/:sub/flash-cards",
    loadComponent: () => import("./feature/pages/flash-cards/flash-cards.component").then((p) => p.FlashCardsComponent),
    title: "Flash Cards",
    canActivate: [loginGuard],
    resolve: { data: flashcardsResolver },
  },
  {
    path: "flashcards/:sub/:subTopic",
    loadComponent: () =>
      import("./feature/pages/collection-flashcards-details/collection-flashcards-details.component").then(
        (p) => p.CollectionFlashcardsDetailsComponent,
      ),
    title: "Flashcards Collection Details",
    canActivate: [loginGuard],
    resolve: { data: flashcardsDetailsResolver },
  },
  {
    path: "flashcards/:topic",
    loadComponent: () =>
      import("./feature/pages/level-flashcards/level-flashcards.component").then((p) => p.LevelFlashcardsComponent),
    title: "Level Flash Cards",
    canActivate: [loginGuard],
    resolve: { data: levelFlashcardsResolver },
  },
  {
    path: "curriculum/:cur/subject/:sub/past-papers",
    loadComponent: () => import("./feature/pages/past-papers/past-papers.component").then((p) => p.PastPapersComponent),
    title: "Past Papers",
    canActivate: [loginGuard],
    resolve: { data: pastpapersResolver },
  },
  {
    path: "past-papers/:sub/:subTopic",
    loadComponent: () =>
      import("./feature/pages/past-papers-details/past-papers-details.component").then(
        (p) => p.PastPapersDetailsComponent,
      ),
    title: "Past Papers Details",
    canActivate: [loginGuard],
    resolve: { data: pastpapersDetailsResolver },
    runGuardsAndResolvers: "paramsOrQueryParamsChange",
  },
  {
    path: "curriculum/:cur/subject/:sub/exam-questions",
    loadComponent: () =>
      import("./feature/pages/exam-question/exam-question.component").then((p) => p.ExamQuestionComponent),
    title: "Exam Questions",
    canActivate: [loginGuard],
    resolve: { data: examQuestionsResolver },
  },
  {
    path: "exam-questions/:sub/:subTopic",
    loadComponent: () =>
      import("./feature/pages/exam-question-details/exam-question-details.component").then(
        (p) => p.ExamQuestionDetailsComponent,
      ),
    title: "Exam Questions Details",
    canActivate: [loginGuard],
    resolve: { data: examQuestionsDetailsResolver },
  },
  {
    path: "exam-questions/:topic",
    loadComponent: () =>
      import("./feature/pages/level-exam-question/level-exam-question.component").then(
        (p) => p.LevelExamQuestionComponent,
      ),
    title: "Level Exam Questions",
    canActivate: [loginGuard],
    resolve: { data: levelExamQuestionsResolver },
  },
  {
    path: "revision-notes-tool",
    loadComponent: () =>
      import("./feature/pages/static-revision-notes/static-revision-notes.component").then(
        (p) => p.StaticRevisionNotesComponent,
      ),
    title: "Revision Notes",

  },
  {
    path: "flash-cards-tool",
    loadComponent: () =>
      import("./feature/pages/static-flash-cards/static-flash-cards.component").then(
        (p) => p.StaticFlashCardsComponent,
      ),
    title: "Flash card",
  },
  {
    path: "exam-question-tool",
    loadComponent: () =>
      import("./feature/pages/static-exam-question/static-exam-question.component").then(
        (p) => p.StaticExamQuestionComponent,
      ),
    title: "Exam Question",
  },
  {
    path: "past-paper-tool",
    loadComponent: () =>
      import("./feature/pages/static-past-papers/static-past-papers.component").then(
        (p) => p.StaticPastPapersComponent,
      ),
    title: "Past Paper",
  },
  {
    path: "student",
    loadComponent: () => import("./core/pages/auth/auth.component").then((p) => p.AuthComponent),
    children: [
      {
        path: "signup",
        loadComponent: () => import("./core/pages/signup/signup.component").then((p) => p.SignupComponent),
        title: "Sign Up",
        canActivate: [logoutGuard],
      },
      {
        path: "login",
        loadComponent: () => import("./core/pages/login/login.component").then((p) => p.LoginComponent),
        title: "Login",
        canActivate: [logoutGuard],
      },
    ],
  },
  {
    path: "not-found",
    loadComponent: () => import("./feature/pages/not-found/not-found.component").then((p) => p.NotFoundComponent),
    title: "Not Found",
  },
  {
    path: "**",
    loadComponent: () => import("./feature/pages/not-found/not-found.component").then((p) => p.NotFoundComponent),
    title: "Not Found",
  },
];
